import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X } from 'lucide-react';
import {
  getLookupValues,
  fetchStates,
  fetchCities,
  fetchCountriesAndCurrencies
} from '../services/lookupService';
import { LOOKUP_GROUPS, API_CONFIG } from '../config/apiConfig'; // Ensure API_CONFIG is imported
import {
  fetchProfileData,
  transformApiProfileToRecord
} from '../services/profileService';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const CompleteProfile = () => {
  const { dispatch } = useAppContext();
  const { token, updateProfileCompletion } = useAuth(); // Get token from auth context
  const navigate = useNavigate();

  // State for the profile form
  const [editedProfile, setEditedProfile] = useState({
    id: '',
    name: '',
    relation: 'Self', // Hardcoded as per logic
    notes: '',
    profileType: 'Self',
    dob: '',
    gender: '',
    maritalStatus: '', // Typo corrected: maretialStatus -> maritalStatus
    country: '',
    state: '',
    city: '',
    currency: '',
    employmentStatus: '',
    retirementAge: 0,
    profilePicture: ''
  });

  // State for dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({
    profileType: [],
    gender: [],
    maritalStatus: [],
    employmentStatus: [],
    countries: [],
    states: [],
    cities: [],
    currencies: []
  });

  const [loading, setLoading] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false); // For save button loading state
  const [operationStatus, setOperationStatus] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  // Effect to load existing profile data
  useEffect(() => {
    const loadProfileData = async () => {
      setLoading(true);
      try {
        const response = await fetchProfileData();
        if (response.data && response.data.length > 0) {
          const userProfile = transformApiProfileToRecord(response.data[0]);
          setEditedProfile(prev => ({
            ...prev,
            ...userProfile,
            profileType: userProfile.profileType || 'Self',
            relation: userProfile.relation || 'Self'
          }));
        }
      } catch (error) {
        console.error('Failed to fetch profile data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProfileData();
  }, []);

  // Effect to fetch initial dropdown data
  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoading(true);
      try {
        const { countries, currencies } = await fetchCountriesAndCurrencies();
        const [profileType, gender, maritalStatus, employmentStatus] = await Promise.all([
          getLookupValues(LOOKUP_GROUPS.PROFILE_TYPE),
          getLookupValues(LOOKUP_GROUPS.GENDER),
          getLookupValues(LOOKUP_GROUPS.MARITAL_STATUS),
          getLookupValues(LOOKUP_GROUPS.EMPLOYMENT_STATUS)
        ]);
        
        setDropdownOptions(prev => ({
          ...prev,
          profileType,
          gender,
          maritalStatus,
          employmentStatus,
          countries,
          currencies
        }));
      } catch (error) {
        console.error('Failed to fetch initial dropdowns:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDropdowns();
  }, []);

  // Effects for dependent dropdowns (States and Cities)
  useEffect(() => {
    if (editedProfile.country) {
      fetchStates(editedProfile.country)
        .then(states => setDropdownOptions(prev => ({ ...prev, states, cities: [] })))
        .catch(err => console.error('Failed to fetch states:', err));
    }
  }, [editedProfile.country]);

  useEffect(() => {
    if (editedProfile.state && editedProfile.country) {
      fetchCities(editedProfile.country, editedProfile.state)
        .then(cities => setDropdownOptions(prev => ({ ...prev, cities })))
        .catch(err => console.error('Failed to fetch cities:', err));
    }
  }, [editedProfile.state, editedProfile.country]);


  // UPDATED SAVE/UPDATE FUNCTION
  const handleProfileSave = async () => {
    if (!editedProfile.name || !editedProfile.profileType || !editedProfile.country || !editedProfile.state || !editedProfile.city || !editedProfile.currency || !editedProfile.employmentStatus) {
      setOperationStatus({ show: true, message: 'Please fill all required fields marked with *', type: 'error' });
      setTimeout(() => setOperationStatus({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    setSaveInProgress(true);

    // Map the component state (camelCase) to the API's expected payload (snake_case)
    const apiPayload = {
      name: editedProfile.name,
      profile_type: editedProfile.profileType,
      gender: editedProfile.gender,
      marital_status: editedProfile.maritalStatus, // Correctly mapped
      date_of_birth: editedProfile.dob,
      country: editedProfile.country,
      state: editedProfile.state,
      city: editedProfile.city,
      currency: editedProfile.currency,
      employment_status: editedProfile.employmentStatus,
      planned_retirement_age: editedProfile.retirementAge,
      relation: editedProfile.relation // This is always 'Self'
    };

    try {
      const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save profile.');
      }

      setOperationStatus({ show: true, message: 'Profile saved successfully! Redirecting...', type: 'success' });
      updateProfileCompletion(true);
      dispatch({ type: 'UPDATE_PROFILE', payload: { ...editedProfile, isProfileComplete: true } });

      setTimeout(() => {
        navigate('/overview');
      }, 2000);

    } catch (error) {
      console.error('Failed to save profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setOperationStatus({ show: true, message: errorMessage, type: 'error' });
      setTimeout(() => setOperationStatus({ show: false, message: '', type: 'success' }), 5000);
    } finally {
      setSaveInProgress(false);
    }
  };

  const renderDropdownOptions = (options, placeholder = 'Select') => {
    if (!options) return <option value="">Loading...</option>;
    return (
      <>
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 w-full max-w-5xl border border-gray-100">
        <h3 className="text-2xl font-semibold text-gray-900 text-center mb-2">
          Complete Your Profile
        </h3>
        <p className="text-center text-gray-500 mb-8">
          Please fill out your details to complete your account setup.
        </p>

        {/* --- FORM GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Name */}
           <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={editedProfile.name} onChange={e => setEditedProfile({ ...editedProfile, name: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
            </div>
            
            {/* Profile Type */}
             <select
                value={editedProfile.profileType}
                disabled
                className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-gray-100 cursor-not-allowed"
              >
                <option value="Self">Self</option>
              </select>
            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select value={editedProfile.gender} onChange={e => setEditedProfile({ ...editedProfile, gender: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg text-sm">
                {renderDropdownOptions(dropdownOptions.gender, 'Select')}
              </select>
            </div>
            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input type="date" value={editedProfile.dob} onChange={e => setEditedProfile({ ...editedProfile, dob: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            {/* Marital Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
              <select value={editedProfile.maritalStatus} onChange={e => setEditedProfile({ ...editedProfile, maritalStatus: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg text-sm">
                {renderDropdownOptions(dropdownOptions.maritalStatus, 'Select')}
              </select>
            </div>
            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
              <select value={editedProfile.country} onChange={e => setEditedProfile({ ...editedProfile, country: e.target.value, state: '', city: '', currency: '' })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                {renderDropdownOptions(dropdownOptions.countries, 'Select Country')}
              </select>
            </div>
            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
              <select value={editedProfile.state} onChange={e => setEditedProfile({ ...editedProfile, state: e.target.value, city: '' })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required disabled={!editedProfile.country}>
                {renderDropdownOptions(dropdownOptions.states, 'Select State')}
              </select>
            </div>
            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <select value={editedProfile.city} onChange={e => setEditedProfile({ ...editedProfile, city: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required disabled={!editedProfile.state}>
                {renderDropdownOptions(dropdownOptions.cities, 'Select City')}
              </select>
            </div>
            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
              <select value={editedProfile.currency} onChange={e => setEditedProfile({ ...editedProfile, currency: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required disabled={!editedProfile.country}>
                {renderDropdownOptions(dropdownOptions.currencies, 'Select Currency')}
              </select>
            </div>
            {/* Employment Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status *</label>
              <select value={editedProfile.employmentStatus} onChange={e => setEditedProfile({ ...editedProfile, employmentStatus: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                {renderDropdownOptions(dropdownOptions.employmentStatus, 'Select')}
              </select>
            </div>
            {/* Retirement Age */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Planned Retirement Age</label>
              <input type="number" value={editedProfile.retirementAge} onChange={e => setEditedProfile({ ...editedProfile, retirementAge: parseInt(e.target.value) || 0 })} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          {/* --- ACTION BUTTONS --- */}
          <div className="flex justify-end space-x-4 mt-8">
            <button
              onClick={() => navigate('/overview')}
              className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 flex items-center transition duration-200"
            >
              <X className="w-5 h-5 mr-2" />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleProfileSave}
              disabled={saveInProgress}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center disabled:bg-blue-400 disabled:cursor-not-allowed transition duration-200"
            >
              <Save className="w-5 h-5 mr-2" />
              <span>{saveInProgress ? 'Saving...' : 'Save Profile'}</span>
            </button>
          </div>
        </div>
      </div>
  )
};

export default CompleteProfile;